import { useEffect, useState } from 'react';
import container from 'src/container';
import { OrganizationDocument } from 'src/domain/organization';

const {
    cradle: { munduClient, storageService },
} = container;

export type PreviewDocument = {
    document: OrganizationDocument;
    file?: File;
};

type PreviewState =
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'ready'; url: string };

export const useDocumentPreview = (
    organizationId: string,
    item: PreviewDocument,
) => {
    const [attempt, setAttempt] = useState(0);
    const [state, setState] = useState<PreviewState>({ status: 'loading' });

    useEffect(() => {
        let active = true;
        let objectUrl: string | undefined;
        const controller = new AbortController();
        const timeout = window.setTimeout(() => {
            active = false;
            controller.abort();
            setState({ status: 'error' });
        }, 30000);
        setState({ status: 'loading' });

        const load = async () => {
            try {
                let blob: Blob;
                if (item.file) {
                    blob = item.file;
                } else {
                    const sas = await munduClient.getOrganizationDocumentsSas(
                        organizationId,
                    );
                    if (!active) return;
                    const client = await storageService.getBlockBlobClient(
                        sas,
                        item.document.blobName,
                    );
                    if (!active) return;
                    const response = await fetch(client.url, {
                        signal: controller.signal,
                    });
                    if (!response.ok) throw new Error('Document unavailable');
                    blob = await response.blob();
                }
                if (!active) return;
                // Use the document's MIME type so PDFs render even if storage
                // returns a generic content type. Blob URLs also allow downloads.
                objectUrl = URL.createObjectURL(
                    blob.slice(0, blob.size, item.document.mimeType),
                );
                setState({ status: 'ready', url: objectUrl });
            } catch {
                if (active) setState({ status: 'error' });
            } finally {
                window.clearTimeout(timeout);
            }
        };
        void load();

        return () => {
            active = false;
            controller.abort();
            window.clearTimeout(timeout);
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [organizationId, item, attempt]);

    return { state, retry: () => setAttempt(current => current + 1) };
};
